import { useAuthStore } from '@/stores/auth'

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1'

/**
 * Fetch an authenticated API path with automatic access-token refresh: attaches
 * the Bearer token and, on a 401, refreshes once (via the stored refresh token)
 * and retries. Returns the raw `Response` for the caller to consume
 * (`.json()` / `.blob()`), or `null` when there is no session at all (guest /
 * signed out) — callers treat that as "stay local".
 *
 * This is what keeps sync + canonical render alive past the 15-minute access
 * token expiry; without it a stale token silently 401s and the app falls back to
 * local drafts.
 */
export async function authedFetch(
  path: string,
  init: { method?: string; body?: unknown; headers?: Record<string, string> } = {}
): Promise<Response | null> {
  const auth = useAuthStore()
  if (!auth.accessToken) return null

  const send = (bearer: string): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      method: init.method ?? 'GET',
      headers: {
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
        Authorization: `Bearer ${bearer}`,
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })

  let res = await send(auth.accessToken)
  if (res.status === 401 && (await auth.refresh()) && auth.accessToken) {
    res = await send(auth.accessToken)
  }
  return res
}
